import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  generatePdfPageThumbnail,
  loadPdfFromUrl,
} from '../../BulkUpload/utils/pdfUtils';
import { isPlaceholderThumbnail } from '../../BulkUpload/utils/thumbnails';

// One page of a larger file, drawn on its own (staging QA 2026-10-30). A
// response routed out of a multi-student upload (Bulk Upload v1 and v2) points
// at the class's whole PDF plus the page it was routed (filePageIndexes);
// embedding the file showed every other student's pages too. This renders
// just that page with pdf.js (fonts without eval, see pdfUtils) and never
// falls back to the whole file.

export const PDF_PAGE_COPY = Object.freeze({
  loading: 'Loading page preview…',
  failed: "We couldn't show this page.",
});

// Pages of the same file share one loaded document; it is released when the
// last of them unmounts.
const documents = new Map();

const acquire = (url) => {
  let entry = documents.get(url);
  if (!entry) {
    entry = { users: 0, promise: loadPdfFromUrl(url) };
    // A failed load is not kept: the next mount tries again.
    entry.promise.catch(() => {
      if (documents.get(url) === entry) documents.delete(url);
    });
    documents.set(url, entry);
  }
  entry.users += 1;
  return entry;
};

const release = (url, entry) => {
  entry.users -= 1;
  if (entry.users > 0 || documents.get(url) !== entry) return;
  documents.delete(url);
  entry.promise.then((pdf) => pdf?.destroy?.()).catch(() => {});
};

/** Test hook: forget every loaded document. */
export const resetPdfPageCache = () => documents.clear();

export default function PdfPagePreview({ url, pageIndex, scale = 1.5 }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(null);
    setFailed(false);
    if (!url || !Number.isInteger(pageIndex) || pageIndex < 0) {
      setFailed(true);
      return undefined;
    }
    let live = true;
    const entry = acquire(url);
    entry.promise
      .then(async (pdf) => {
        if (!live) return;
        if (pageIndex + 1 > (pdf?.numPages || 0))
          throw new Error('No such page');
        const image = await generatePdfPageThumbnail(pdf, pageIndex + 1, scale);
        if (isPlaceholderThumbnail(image))
          throw new Error('Page did not render');
        if (live) setSrc(image);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
      release(url, entry);
    };
  }, [url, pageIndex, scale]);

  if (failed) return <div role="status">{PDF_PAGE_COPY.failed}</div>;
  if (!src) return <div role="status">{PDF_PAGE_COPY.loading}</div>;
  return (
    <PageImage
      src={src}
      alt={`Page ${pageIndex + 1}`}
      data-testid="pdf-page-preview"
    />
  );
}

const PageImage = styled.img`
  width: 100%;
`;
